import React, { useEffect, useMemo, useState } from 'react';
import { entradasComparaveis, valorParaTexto } from '../../../utils/numeroLab';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { listValidationExperiments } from '../../../infra/api/ValidationPipelineApi';
import { getLabResultByExperimentId } from '../../../infra/api/labResultApi';
import { getExperimentos } from '../../../infra/api/ExperimentoApi';
import {
    deleteTrainingJob,
    getTrainingJob,
    getTrainingJobResult,
    listTrainingJobs,
    startTrainingJob,
} from '../../../infra/api/TrainingJobsApi';
import { useBootstrap } from '../../../context/BootstrapContext';

function pick(obj, ...keys) {
    for (const k of keys) {
        if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
    }
    return undefined;
}

function normalizeId(val) {
    if (!val) return '';
    if (typeof val === 'string') {
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch {
            // string comum
        }
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
}

function normalizeJobId(job) {
    const raw = job?.id || job?.jobId || job?._id || job?.ID;
    if (!raw) return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object') {
        if (raw.$oid) return String(raw.$oid);
        if (raw.id) return normalizeJobId({ id: raw.id });
        if (raw.jobId) return normalizeJobId({ id: raw.jobId });
        if (raw._id) return normalizeJobId({ id: raw._id });
    }
    return String(raw);
}

function parseDateSafe(value) {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function parseEpochSeconds(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    const millis = n > 1e12 ? n : n * 1000;
    const d = new Date(millis);
    return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(value) {
    const d = parseDateSafe(value);
    if (!d) return '-';
    return d.toLocaleString('pt-BR');
}

function toInputDate(value) {
    const d = parseDateSafe(value);
    if (!d) return '-';
    return d.toISOString().slice(0, 10);
}

function fromInputDate(value, endOfDay = false) {
    if (!value) return null;
    const suffix = endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z';
    return parseDateSafe(`${value}${suffix}`);
}

function toRfc3339RangeDate(inputDate, endOfDay = false) {
    if (!inputDate) return '';
    return `${inputDate}${endOfDay ? 'T23:59:59.999Z' : 'T00:00:00.000Z'}`;
}

function currentTenant() {
    const [, maybe] = window.location.pathname.split('/');
    return maybe || null;
}

function graphPathForExperiment(experimentId) {
    const tenant = currentTenant();
    if (!tenant || !experimentId || experimentId === '—') return '';
    return `/${tenant}/graficos/${encodeURIComponent(experimentId)}`;
}

function fallbackExperimentForGraph(row) {
    return {
        id: row?.experimentId,
        serialNumber: '—',
        protocolName: 'Protocolo não informado',
        analysisName: row?.analysisId && row.analysisId !== '—' ? row.analysisId : 'Análise não informada',
        startDate: null,
        endDate: null,
        status: 'finished',
        protocolColor: '#607d8b',
    };
}

function detectFluxType(row) {
    const raw = [
        row?.analysisId,
        row?.analysisName,
        row?.analysisDisplay,
        row?.analysisIdentificacao,
        row?.nomeAnalise,
        row?.analysisLabel,
        row?.contextAnalysisName,
        row?.contextAnalysisId,
        row?.protocolName,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (raw.includes('afluente') || raw.includes('entrada') || raw.includes('inlet')) return 'Afluente';
    if (raw.includes('efluente') || raw.includes('saida') || raw.includes('saída') || raw.includes('outlet')) return 'Efluente';
    return 'Não classificado';
}

function parseAnalysisMeta(exp) {
    const analysis = exp?.analysis && typeof exp.analysis === 'object' ? exp.analysis : null;
    const analise = exp?.analise && typeof exp.analise === 'object' ? exp.analise : null;
    const context = exp?.context && typeof exp.context === 'object' ? exp.context : null;

    const analysisId = String(
        pick(
            exp,
            'analysisId',
            'analiseId',
            'idAnalise',
            'analysis_id',
            'analise_id',
        )
        || pick(analysis, 'id', 'analysisId', 'analiseId')
        || pick(analise, 'id', 'analysisId', 'analiseId')
        || pick(context, 'analysisId', 'analiseId', 'idAnalise')
        || '',
    ).trim();

    const analysisName = String(
        pick(
            exp,
            'analysisName',
            'analiseNome',
            'analysisLabel',
            'nomeAnalise',
            'analysis_name',
            'analise_nome',
        )
        || pick(analysis, 'name', 'nome', 'label', 'description', 'descricao')
        || pick(analise, 'name', 'nome', 'label', 'description', 'descricao')
        || pick(context, 'analysisName', 'analiseNome', 'analysisLabel', 'nomeAnalise')
        || '',
    ).trim();

    return {
        analysisId: analysisId || '—',
        analysisName,
        contextAnalysisId: String(pick(context, 'analysisId', 'analiseId', 'idAnalise') || '').trim(),
        contextAnalysisName: String(pick(context, 'analysisName', 'analiseNome', 'analysisLabel', 'nomeAnalise') || '').trim(),
    };
}

function hasBothLabResults(lab) {
    if (!lab?.bacterias) return false;
    // Indexar pelas COMPARAVEIS (NMP): o labResult aceita uma entrada por bacteria x
    // unidade, e no array cru `[1]` pode ser o NMP do Coliformes em vez da E. coli.
    // Registro legado (sem `unit`) e todo comparavel, entao nada muda para ele.
    const comp = entradasComparaveis(lab.bacterias);
    return Number.isFinite(Number(comp[0]?.nmp)) && Number.isFinite(Number(comp[1]?.nmp));
}

function rowDate(row) {
    return (
        parseDateSafe(row?.executionStartAt)
        || parseDateSafe(row?.executionEndAt)
        || parseDateSafe(row?.experimentDate)
        || parseDateSafe(row?.updatedAt)
        || parseDateSafe(row?.createdAt)
        || null
    );
}

function statusIaMeta(value) {
    if (value === true) return { label: 'Válido IA', color: 'success' };
    if (value === false) return { label: 'Inválido IA', color: 'error' };
    return { label: 'Sem validação IA', color: 'default' };
}

function StatBox({ label, value, color }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center', borderColor: color || 'divider' }}>
            <Typography variant="caption" color={color || 'text.secondary'} display="block">
                {label}
            </Typography>
            <Typography variant="h6" fontWeight={700} color={color || 'text.primary'}>
                {value ?? '—'}
            </Typography>
        </Paper>
    );
}

export default function TabValidosIA() {
    const navigate = useNavigate();
    const { bootstrap } = useBootstrap();

    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [rows, setRows] = useState([]);
    const [labMap, setLabMap] = useState({});

    const [filterFlow, setFilterFlow] = useState('todos');
    const [filterLab, setFilterLab] = useState('todos');
    const [filterIaStatus, setFilterIaStatus] = useState('todos');
    const [filterAnalysis, setFilterAnalysis] = useState('');
    const [filterExperimentId, setFilterExperimentId] = useState('');
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');

    const [tablePage, setTablePage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [selectedExperimentIds, setSelectedExperimentIds] = useState([]);
    const [trainingLoading, setTrainingLoading] = useState(false);
    const [lastTrainingJobId, setLastTrainingJobId] = useState('');
    const [trainingFeedback, setTrainingFeedback] = useState('');
    const [trainingJobs, setTrainingJobs] = useState([]);
    const [trainingJobsLoading, setTrainingJobsLoading] = useState(false);
    const [trainingStatusView, setTrainingStatusView] = useState(null);
    const [trainingResultView, setTrainingResultView] = useState(null);

    const normalizeTrainingError = (error, fallbackMessage) => {
        const raw = error?.response?.data?.error || error?.message || fallbackMessage;
        const text = String(raw || '');
        if (text.toLowerCase().includes('full ou period')) {
            return 'Backend de treino desatualizado: este front exige suporte a mode=selected em /training/jobs.';
        }
        return text || fallbackMessage;
    };

    const protocolById = useMemo(() => {
        const map = {};
        const protocols = Array.isArray(bootstrap?.protocolos) ? bootstrap.protocolos : [];
        protocols.forEach((p) => {
            const pid = normalizeId(p?.id || p?._id);
            if (pid) map[pid] = p;
        });
        return map;
    }, [bootstrap]);

    const analysisById = useMemo(() => {
        const map = {};
        const protocols = Array.isArray(bootstrap?.protocolos) ? bootstrap.protocolos : [];
        protocols.forEach((p) => {
            const analyses = Array.isArray(p?.analysis) ? p.analysis : [];
            analyses.forEach((a, index) => {
                const aid = normalizeId(a?.id || a?._id) || String(a?.name || a?.nome || `analysis-${index}`);
                if (!aid) return;
                map[aid] = {
                    id: aid,
                    name: String(a?.name || a?.nome || ''),
                    identificacao: String(a?.identificacao || ''),
                    protocolName: String(p?.name || p?.nome || ''),
                };
            });
        });
        return map;
    }, [bootstrap]);

    const loadRows = async () => {
        setLoading(true);
        setErro('');
        try {
            const [validations, javaExperiments] = await Promise.all([
                listValidationExperiments({ limit: 5000 }),
                getExperimentos({}, true).catch(() => []),
            ]);

            const validationMap = {};
            (Array.isArray(validations) ? validations : []).forEach((v) => {
                const experimentId = String(pick(v, 'experimentId', 'ExperimentId') || '').trim();
                if (!experimentId) return;
                const updatedAt = parseDateSafe(pick(v, 'updatedAt', 'UpdatedAt'));
                const current = validationMap[experimentId];
                if (!current) {
                    validationMap[experimentId] = v;
                    return;
                }
                const currentDate = parseDateSafe(pick(current, 'updatedAt', 'UpdatedAt'));
                if (updatedAt && (!currentDate || updatedAt > currentDate)) {
                    validationMap[experimentId] = v;
                }
            });

            const mapped = (Array.isArray(javaExperiments) ? javaExperiments : [])
                .map((exp) => {
                    const experimentId = String(pick(exp, 'id', 'experimentId', '_id') || '').trim();
                    if (!experimentId) return null;

                    const executionStartAt = parseEpochSeconds(pick(exp, 'startDate'));
                    const executionEndAt = parseEpochSeconds(pick(exp, 'endDate'));
                    const analysisMeta = parseAnalysisMeta(exp);
                    const protocolId = normalizeId(pick(exp, 'protocolId', 'idProtocolo', 'protocol_id'));
                    const protocol = protocolById[protocolId] || null;
                    const resolvedAnalysis = analysisById[analysisMeta.analysisId] || null;

                    const analysisId = analysisMeta.analysisId;
                    const analysisName = analysisMeta.analysisName
                        || resolvedAnalysis?.name
                        || resolvedAnalysis?.identificacao
                        || '';
                    const analysisIdentificacao = resolvedAnalysis?.identificacao || '';
                    const protocolName = String(protocol?.name || protocol?.nome || resolvedAnalysis?.protocolName || '');
                    const analysisDisplay = analysisName
                        ? `${analysisId} - ${analysisName}`
                        : analysisId;

                    const v = validationMap[experimentId];
                    const iaValido = v && typeof v.valido === 'boolean' ? Boolean(v.valido) : null;

                    return {
                        id: experimentId,
                        experimentId,
                        analysisId,
                        analysisName,
                        analysisIdentificacao,
                        analysisDisplay,
                        protocolName,
                        contextAnalysisId: analysisMeta.contextAnalysisId,
                        contextAnalysisName: analysisMeta.contextAnalysisName,
                        flowType: detectFluxType({
                            analysisId,
                            analysisName,
                            analysisIdentificacao,
                            analysisDisplay,
                            protocolName,
                            contextAnalysisId: analysisMeta.contextAnalysisId,
                            contextAnalysisName: analysisMeta.contextAnalysisName,
                        }),
                        iaValido,
                        iaUpdatedAt: pick(v, 'updatedAt', 'UpdatedAt') || null,
                        executionStartAt: executionStartAt ? executionStartAt.toISOString() : null,
                        executionEndAt: executionEndAt ? executionEndAt.toISOString() : null,
                        updatedAt: executionEndAt
                            ? executionEndAt.toISOString()
                            : executionStartAt
                                ? executionStartAt.toISOString()
                                : null,
                        experimentDate: executionStartAt ? executionStartAt.toISOString() : null,
                    };
                })
                .filter(Boolean);

            setRows(mapped);

            const uniqueExperimentIds = [...new Set(mapped.map((r) => r.experimentId).filter(Boolean))];

            const labEntries = await Promise.all(
                uniqueExperimentIds.map(async (experimentId) => {
                    try {
                        const lab = await getLabResultByExperimentId(experimentId);
                        return [experimentId, lab || null];
                    } catch {
                        return [experimentId, null];
                    }
                }),
            );

            const nextLabMap = {};
            for (const [experimentId, lab] of labEntries) {
                nextLabMap[experimentId] = lab;
            }
            setLabMap(nextLabMap);

            const javaStartDates = (Array.isArray(javaExperiments) ? javaExperiments : [])
                .map((exp) => parseEpochSeconds(pick(exp, 'startDate')))
                .filter((d) => d instanceof Date);

            const javaEndDates = (Array.isArray(javaExperiments) ? javaExperiments : [])
                .map((exp) => parseEpochSeconds(pick(exp, 'endDate')) || parseEpochSeconds(pick(exp, 'startDate')))
                .filter((d) => d instanceof Date);

            if (javaStartDates.length > 0) {
                const minDate = new Date(Math.min(...javaStartDates.map((d) => d.getTime())));
                const maxSource = javaEndDates.length > 0 ? javaEndDates : javaStartDates;
                const maxDate = new Date(Math.max(...maxSource.map((d) => d.getTime())));
                setFilterDateStart(toInputDate(minDate));
                setFilterDateEnd(toInputDate(maxDate));
            } else {
                const fallbackDates = mapped.map((r) => rowDate(r)).filter((d) => d instanceof Date);
                if (fallbackDates.length > 0) {
                    const minDate = new Date(Math.min(...fallbackDates.map((d) => d.getTime())));
                    const maxDate = new Date(Math.max(...fallbackDates.map((d) => d.getTime())));
                    setFilterDateStart(toInputDate(minDate));
                    setFilterDateEnd(toInputDate(maxDate));
                } else {
                    setFilterDateStart('');
                    setFilterDateEnd('');
                }
            }

            setTablePage(0);
        } catch (e) {
            setErro(e?.response?.data?.error || e?.message || 'Erro ao carregar válidos da IA');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRows();
    }, [bootstrap]);

    const consultedRow = useMemo(() => {
        const q = filterExperimentId.trim().toLowerCase();
        if (!q) return null;
        return rows.find((r) => String(r.experimentId || '').toLowerCase() === q) || null;
    }, [rows, filterExperimentId]);

    const filteredRows = useMemo(() => {
        const dateStart = fromInputDate(filterDateStart, false);
        const dateEnd = fromInputDate(filterDateEnd, true);

        return rows.filter((row) => {
            const analysis = String(
                `${row.analysisId || ''} ${row.analysisName || ''} ${row.analysisDisplay || ''} ${row.analysisIdentificacao || ''} ${row.protocolName || ''}`,
            ).toLowerCase();
            const flowType = row.flowType;
            const lab = labMap[row.experimentId] || null;
            const date = parseDateSafe(row.executionStartAt) || rowDate(row);
            const ia = row.iaValido;

            if (filterFlow !== 'todos' && flowType !== filterFlow) return false;
            if (filterIaStatus === 'validos' && ia !== true) return false;
            if (filterIaStatus === 'invalidos' && ia !== false) return false;
            if (filterIaStatus === 'sem' && ia !== null) return false;

            if (filterLab === 'comLab' && !hasBothLabResults(lab)) return false;
            if (filterLab === 'semLab' && lab) return false;
            const labComp = entradasComparaveis(lab?.bacterias);
            if (filterLab === 'comCt' && !(lab && Number.isFinite(Number(labComp?.[0]?.nmp)))) return false;
            if (filterLab === 'comEcoli' && !(lab && Number.isFinite(Number(labComp?.[1]?.nmp)))) return false;

            if (filterAnalysis.trim()) {
                const q = filterAnalysis.trim().toLowerCase();
                if (!analysis.includes(q)) return false;
            }

            if (filterExperimentId.trim()) {
                const q = filterExperimentId.trim().toLowerCase();
                if (!String(row.experimentId || '').toLowerCase().includes(q)) return false;
            }

            if (dateStart && date && date < dateStart) return false;
            if (dateEnd && date && date > dateEnd) return false;

            return true;
        });
    }, [rows, labMap, filterFlow, filterIaStatus, filterLab, filterAnalysis, filterExperimentId, filterDateStart, filterDateEnd]);

    const countValidosIa = filteredRows.filter((r) => r.iaValido === true).length;
    const countInvalidosIa = filteredRows.filter((r) => r.iaValido === false).length;
    const countSemValidacaoIa = filteredRows.filter((r) => r.iaValido === null).length;
    const countComLab = filteredRows.filter((r) => hasBothLabResults(labMap[r.experimentId])).length;

    const pagedRows = useMemo(() => {
        const start = tablePage * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, tablePage, rowsPerPage]);

    const selectedCount = selectedExperimentIds.length;
    const allPageSelected = pagedRows.length > 0 && pagedRows.every((r) => selectedExperimentIds.includes(r.experimentId));

    const selectAllFiltered = () => {
        setSelectedExperimentIds([...new Set(filteredRows.map((r) => r.experimentId).filter(Boolean))]);
    };

    const clearSelection = () => {
        setSelectedExperimentIds([]);
    };

    const toggleSelectOne = (experimentId) => {
        setSelectedExperimentIds((prev) => {
            if (prev.includes(experimentId)) {
                return prev.filter((id) => id !== experimentId);
            }
            return [...prev, experimentId];
        });
    };

    const toggleSelectPage = () => {
        const pageIds = pagedRows.map((r) => r.experimentId);
        setSelectedExperimentIds((prev) => {
            if (allPageSelected) {
                return prev.filter((id) => !pageIds.includes(id));
            }
            return [...new Set([...prev, ...pageIds])];
        });
    };

    const submitTraining = async () => {
        const targetExperimentIds = [...new Set(selectedExperimentIds.filter(Boolean))];
        if (targetExperimentIds.length === 0) {
            setTrainingFeedback('Selecione pelo menos um experimento para enviar ao treino IA.');
            return;
        }

        const selectedSet = new Set(targetExperimentIds);
        const selectedRows = rows.filter((r) => selectedSet.has(r.experimentId) || targetExperimentIds.includes(r.experimentId));
        const uniqueAnalysisIds = [...new Set(selectedRows.map((r) => r.analysisId).filter((id) => id && id !== '—'))];
        const dataInicio = toRfc3339RangeDate(filterDateStart, false);
        const dataFim = toRfc3339RangeDate(filterDateEnd, true);

        setTrainingLoading(true);
        setTrainingFeedback('');
        try {
            const res = await startTrainingJob({
                analysisId: uniqueAnalysisIds.length === 1 ? uniqueAnalysisIds[0] : '',
                experimentIds: targetExperimentIds,
                parameters: {},
                mode: 'selected',
                dataInicio,
                dataFim,
            });
            const jobId = res?.jobId || '';
            setLastTrainingJobId(jobId);
            setTrainingFeedback(jobId ? `Treino iniciado com sucesso. Job: ${jobId}` : 'Treino iniciado com sucesso.');
            await loadTrainingJobs();
        } catch (e) {
            setTrainingFeedback(normalizeTrainingError(e, 'Falha ao iniciar treinamento IA'));
        } finally {
            setTrainingLoading(false);
        }
    };
    const loadTrainingJobs = async () => {
        setTrainingJobsLoading(true);
        try {
            const jobs = await listTrainingJobs();
            setTrainingJobs(Array.isArray(jobs) ? jobs : []);
        } catch (e) {
            setTrainingFeedback(e?.response?.data?.error || e?.message || 'Falha ao listar treinamentos');
        } finally {
            setTrainingJobsLoading(false);
        }
    };

    const viewTrainingStatus = async (jobId) => {
        try {
            const data = await getTrainingJob(jobId);
            setTrainingStatusView(data);
        } catch (e) {
            setTrainingFeedback(e?.response?.data?.error || e?.message || 'Falha ao consultar status do job');
        }
    };

    const viewTrainingResult = async (jobId) => {
        try {
            const data = await getTrainingJobResult(jobId);
            setTrainingResultView(data);
        } catch (e) {
            setTrainingFeedback(e?.response?.data?.error || e?.message || 'Falha ao consultar resultado do job');
        }
    };

    const retrainFromJob = async (job) => {
        setTrainingLoading(true);
        try {
            const jobId = normalizeJobId(job);
            const selectedIds = Array.isArray(job?.selectedExperimentIds)
                ? job.selectedExperimentIds.filter(Boolean)
                : [];

            if (!jobId || selectedIds.length === 0) {
                setTrainingFeedback('Este job nao possui experimentos selecionados para retreinar.');
                return;
            }

            const res = await startTrainingJob({
                analysisId: job?.analysisId || job?.metrics?.analysisId || '',
                experimentIds: selectedIds,
                parameters: job?.parameters || {},
                mode: 'selected',
                dataInicio: toRfc3339RangeDate(filterDateStart, false),
                dataFim: toRfc3339RangeDate(filterDateEnd, true),
            });
            const newJobId = res?.jobId || '';
            setLastTrainingJobId(newJobId);
            setTrainingFeedback(newJobId ? `Retreino iniciado. Job: ${newJobId}` : 'Retreino iniciado com sucesso.');
            await loadTrainingJobs();
        } catch (e) {
            setTrainingFeedback(normalizeTrainingError(e, 'Falha ao iniciar retreino'));
        } finally {
            setTrainingLoading(false);
        }
    };
    const removeTrainingJob = async (jobId) => {
        try {
            await deleteTrainingJob(jobId);
            setTrainingFeedback(`Job ${jobId} removido com sucesso.`);
            await loadTrainingJobs();
        } catch (e) {
            setTrainingFeedback(e?.response?.data?.error || e?.message || 'Falha ao remover job');
        }
    };

    useEffect(() => {
        loadTrainingJobs();
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
            <Typography variant="h6" fontWeight={700}>Válidos IA</Typography>
            <Typography variant="body2" color="text.secondary">
                Consulta experimentos da API Java, cruza por experimentId com validação IA da API Go e marca o status (verde/vermelho).
            </Typography>

            {loading && <Alert severity="info">Carregando válidos da IA...</Alert>}
            {erro && <Alert severity="error">{erro}</Alert>}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                    label="Consulta experimentId"
                    size="small"
                    value={filterExperimentId}
                    onChange={(e) => {
                        setFilterExperimentId(e.target.value);
                        setTablePage(0);
                    }}
                    sx={{ minWidth: 260 }}
                />

                <TextField
                    label="Filtro IA"
                    size="small"
                    select
                    value={filterIaStatus}
                    onChange={(e) => {
                        setFilterIaStatus(e.target.value);
                        setTablePage(0);
                    }}
                    sx={{ minWidth: 170 }}
                >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="validos">Válidos IA</MenuItem>
                    <MenuItem value="invalidos">Inválidos IA</MenuItem>
                    <MenuItem value="sem">Sem validação IA</MenuItem>
                </TextField>

                <TextField
                    label="Filtro Fluxo"
                    size="small"
                    select
                    value={filterFlow}
                    onChange={(e) => {
                        setFilterFlow(e.target.value);
                        setTablePage(0);
                    }}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="Afluente">Afluente</MenuItem>
                    <MenuItem value="Efluente">Efluente</MenuItem>
                    <MenuItem value="Não classificado">Não classificado</MenuItem>
                </TextField>

                <TextField
                    label="Filtro Lab Result"
                    size="small"
                    select
                    value={filterLab}
                    onChange={(e) => {
                        setFilterLab(e.target.value);
                        setTablePage(0);
                    }}
                    sx={{ minWidth: 210 }}
                >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="comLab">Com Lab Result (CT + E. coli)</MenuItem>
                    <MenuItem value="semLab">Sem Lab Result</MenuItem>
                    <MenuItem value="comCt">Com CT (coliformesTotaisNmp)</MenuItem>
                    <MenuItem value="comEcoli">Com E. coli (ecoliNmp)</MenuItem>
                </TextField>

                <TextField
                    label="Filtro AnalysisId"
                    size="small"
                    value={filterAnalysis}
                    onChange={(e) => {
                        setFilterAnalysis(e.target.value);
                        setTablePage(0);
                    }}
                />

                <TextField
                    label="Data Início"
                    type="date"
                    size="small"
                    value={filterDateStart}
                    onChange={(e) => {
                        setFilterDateStart(e.target.value);
                        setTablePage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Data Fim"
                    type="date"
                    size="small"
                    value={filterDateEnd}
                    onChange={(e) => {
                        setFilterDateEnd(e.target.value);
                        setTablePage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                />

                <Button variant="outlined" onClick={loadRows} disabled={loading}>
                    {loading ? <CircularProgress size={16} /> : 'Atualizar'}
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ minWidth: 180 }}>Treinamento IA</Typography>
                <Typography variant="body2" color="text.secondary">Selecionados: {selectedCount}</Typography>
                <Button size="small" variant="text" onClick={selectAllFiltered} disabled={filteredRows.length === 0}>Selecionar filtrados</Button>
                <Button size="small" variant="text" onClick={clearSelection} disabled={selectedCount === 0}>Limpar seleção</Button>
                <Button
                    variant="contained"
                    onClick={submitTraining}
                    disabled={trainingLoading || filteredRows.length === 0}
                >
                    {trainingLoading ? <CircularProgress size={16} color="inherit" /> : 'Enviar para treino IA'}
                </Button>
                {lastTrainingJobId && (
                    <Typography variant="caption" color="text.secondary">jobId: {lastTrainingJobId}</Typography>
                )}
                {trainingFeedback && (
                    <Alert severity={trainingFeedback.toLowerCase().includes('falha') || trainingFeedback.toLowerCase().includes('inválido') ? 'error' : 'success'} sx={{ py: 0 }}>
                        {trainingFeedback}
                    </Alert>
                )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2">Lista de treinamentos</Typography>
                    <Button size="small" variant="outlined" onClick={loadTrainingJobs} disabled={trainingJobsLoading}>
                        {trainingJobsLoading ? <CircularProgress size={14} /> : 'Atualizar lista'}
                    </Button>
                </Box>

                {trainingJobs.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Nenhum job de treinamento encontrado.</Typography>
                )}

                {trainingJobs.slice(0, 10).map((job) => {
                    const jobId = normalizeJobId(job);
                    return (
                        <Paper key={jobId || String(Math.random())} variant="outlined" sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 220 }}>jobId: {jobId || '—'}</Typography>
                            <Chip size="small" label={String(job?.status || 'unknown')} color={job?.status === 'completed' ? 'success' : job?.status === 'error' ? 'error' : 'default'} />
                            <Typography variant="caption" color="text.secondary">{job?.processed ?? job?.processados ?? 0}/{job?.total ?? 0}</Typography>
                            <Button size="small" variant="outlined" onClick={() => viewTrainingStatus(jobId)} disabled={!jobId}>Visualizar</Button>
                            <Button size="small" variant="outlined" onClick={() => viewTrainingResult(jobId)} disabled={!jobId}>Resultado</Button>
                            <Button size="small" variant="outlined" onClick={() => retrainFromJob(job)}>Retreinar</Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => removeTrainingJob(jobId)} disabled={!jobId}>Deletar</Button>
                        </Paper>
                    );
                })}

                {trainingStatusView && (
                    <Alert severity={trainingStatusView?.status === 'unknown' ? 'warning' : 'info'}>
                        Status job {trainingStatusView?.jobId || '—'}: {trainingStatusView?.status || 'indisponível'} - {trainingStatusView?.progress ?? 0}% ({trainingStatusView?.processed ?? 0}/{trainingStatusView?.total ?? 0})
                    </Alert>
                )}

                {trainingResultView && (
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: Array.isArray(trainingResultView?.alerts) && trainingResultView.alerts.length > 0 ? 'rgba(255, 152, 0, 0.08)' : 'rgba(76, 175, 80, 0.08)' }}>
                        <Typography variant="body2" fontWeight={700}>{trainingResultView?.summary || 'Resultado carregado.'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Treinados: {trainingResultView?.metrics?.trainedExperiments ?? 0} | Curvas: {trainingResultView?.metrics?.experimentsWithCurves ?? 0} | Pontos de curva: {trainingResultView?.metrics?.totalCurvePoints ?? 0}
                        </Typography>
                        {Array.isArray(trainingResultView?.alerts) && trainingResultView.alerts.length > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                                Alertas: {trainingResultView.alerts.join(' | ')}
                            </Typography>
                        )}

                        {Array.isArray(trainingResultView?.experimentSummaries) && trainingResultView.experimentSummaries.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight={700}>Mini resumo por experimento</Typography>
                                {trainingResultView.experimentSummaries.slice(0, 10).map((s) => (
                                    <Typography key={String(s?.experimentId || Math.random())} variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {s?.experimentId} | pontos: {s?.curvePoints ?? 0} | CT: {s?.ctFirst ?? '—'} {'->'} {s?.ctLast ?? '—'} | E.coli: {s?.ecoliFirst ?? '—'} {'->'} {s?.ecoliLast ?? '—'}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Paper>
                )}
            </Paper>

            {filterExperimentId.trim() && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderColor: consultedRow?.iaValido === true ? 'success.main' : consultedRow?.iaValido === false ? 'error.main' : 'divider',
                        backgroundColor: consultedRow?.iaValido === true
                            ? 'rgba(46, 125, 50, 0.06)'
                            : consultedRow?.iaValido === false
                                ? 'rgba(211, 47, 47, 0.06)'
                                : 'inherit',
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>Consulta de experimento</Typography>
                    {!consultedRow && (
                        <Typography variant="body2" color="text.secondary">Nenhum experimento encontrado para o ID informado.</Typography>
                    )}
                    {consultedRow && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{consultedRow.experimentId}</Typography>
                            <Chip size="small" {...statusIaMeta(consultedRow.iaValido)} />
                            <Typography variant="caption" color="text.secondary">Data início: {fmtDateTime(consultedRow.executionStartAt || consultedRow.experimentDate)}</Typography>
                            <Typography variant="caption" color="text.secondary">Data fim: {fmtDateTime(consultedRow.executionEndAt || consultedRow.updatedAt)}</Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Lucide.ExternalLink size={18} />}
                                onClick={() => {
                                    const href = graphPathForExperiment(consultedRow.experimentId);
                                    if (!href) return;
                                    navigate(href, { state: { experiment: fallbackExperimentForGraph(consultedRow) } });
                                }}
                                disabled={!graphPathForExperiment(consultedRow.experimentId)}
                            >
                                Ver gráfico
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <StatBox label="Consultados (filtro)" value={filteredRows.length} />
                <StatBox label="Válidos IA" value={countValidosIa} color="success.main" />
                <StatBox label="Inválidos IA" value={countInvalidosIa} color="error.main" />
                <StatBox label="Sem validação IA" value={countSemValidacaoIa} color="text.secondary" />
                <StatBox label="Com Lab Result" value={countComLab} color="success.main" />
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allPageSelected}
                                    indeterminate={!allPageSelected && pagedRows.some((r) => selectedExperimentIds.includes(r.experimentId))}
                                    onChange={toggleSelectPage}
                                />
                            </TableCell>
                            <TableCell>Experimento ID</TableCell>
                            <TableCell>Análise (ID / Nome)</TableCell>
                            <TableCell>Status IA</TableCell>
                            <TableCell>Lab Result</TableCell>
                            <TableCell>CT (NMP)</TableCell>
                            <TableCell>E. coli (NMP)</TableCell>
                            <TableCell>Data Lab</TableCell>
                            <TableCell>Atualizado em</TableCell>
                            <TableCell>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={10}>
                                    <Typography variant="body2" color="text.secondary">
                                        Sem registros válidos para os filtros atuais.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {pagedRows.map((row) => {
                            const lab = labMap[row.experimentId] || null;
                            const iaMeta = statusIaMeta(row.iaValido);
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={selectedExperimentIds.includes(row.experimentId)}
                                            onChange={() => toggleSelectOne(row.experimentId)}
                                        />
                                    </TableCell>
                                    <TableCell>{row.experimentId}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.analysisId}</Typography>
                                        <Typography variant="caption" color="text.secondary">{row.analysisName || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={iaMeta.label} color={iaMeta.color} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={lab ? 'Com' : 'Sem'} color={lab ? 'success' : 'default'} />
                                    </TableCell>
                                    <TableCell>{valorParaTexto(entradasComparaveis(lab?.bacterias)?.[0]?.nmp) || '—'}</TableCell>
                                    <TableCell>{valorParaTexto(entradasComparaveis(lab?.bacterias)?.[1]?.nmp) || '—'}</TableCell>
                                    <TableCell>{lab?.analysisDate ? fmtDateTime(lab.analysisDate) : '—'}</TableCell>
                                    <TableCell>{fmtDateTime(row.iaUpdatedAt || row.updatedAt)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Lucide.ExternalLink size={18} />}
                                            onClick={() => {
                                                const href = graphPathForExperiment(row.experimentId);
                                                if (!href) return;
                                                navigate(href, { state: { experiment: fallbackExperimentForGraph(row) } });
                                            }}
                                            disabled={!graphPathForExperiment(row.experimentId)}
                                        >
                                            Ver gráfico
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={tablePage}
                    onPageChange={(_, newPage) => setTablePage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setTablePage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                />
            </TableContainer>
        </Box>
    );
}




